import { z } from "zod";
import { accessFields, actorSchema, idSchema, instantSchema, moneySchema } from "@amber/contracts";
import { actionNoteSchema, countSchema, httpUrlSchema, intervalSchema, platformSchema, slugSchema } from "./shared.js";

export const COLLECTION_STATES = ["collected", "known_not_collected", "future_only"] as const;
export const collectionStateSchema = z.enum(COLLECTION_STATES);

export const platformRecordSchema = z
  .strictObject({
    platform: platformSchema,
    support_phase: z.number().int().min(1).max(3).nullable(),
    collection_state: collectionStateSchema,
    url_patterns: z.array(z.string().min(1)),
  })
  .refine((p) => (p.collection_state === "future_only") === (p.support_phase === null), {
    message: "future_only platforms, and only they, have no support phase",
    path: ["support_phase"],
  });
export type PlatformRecord = z.infer<typeof platformRecordSchema>;

export const SOURCE_KINDS = [
  "channel",
  "playlist",
  "programme",
  "feed",
  "account",
  "website",
  "official_record",
  "news_search",
] as const;
export const sourceKindSchema = z.enum(SOURCE_KINDS);

export const CADENCE_TIERS = ["priority", "standard", "low"] as const;
export const cadenceTierSchema = z.enum(CADENCE_TIERS);

export const MONITORING_STATES = ["not_in_any_plan", "monitored", "paused", "not_collected"] as const;
export const monitoringStateSchema = z.enum(MONITORING_STATES);

export const HEALTH_STATES = ["failing", "partial", "late", "unusually_quiet", "healthy"] as const;
export const healthStateSchema = z.enum(HEALTH_STATES);

export const BREAK_KINDS = ["recorded_break", "expected_quiet"] as const;
export const breakKindSchema = z.enum(BREAK_KINDS);

export const publishingBreakSchema = z.strictObject({
  id: idSchema("brk"),
  kind: breakKindSchema,
  from: z.iso.date(),
  to: z.iso.date().nullable(),
  reason: z.string().min(1),
  by: actorSchema,
  ended_at: instantSchema.nullable(),
});

export const sourceHealthSchema = z.strictObject({
  state: healthStateSchema,
  quiet_latched_at: instantSchema.nullable(),
  computed_at: instantSchema,
});

export const sourceRecordSchema = z
  .strictObject({
    id: idSchema("src"),
    platform: platformSchema,
    kind: sourceKindSchema,
    canonical_url: httpUrlSchema,
    title: z.string().min(1),
    publisher_organisation_id: idSchema("org").nullable(),
    programme_of: idSchema("src").nullable(),
    cadence_tier: cadenceTierSchema,
    quota_group: slugSchema.nullable(),
    known_breaks: z.array(publishingBreakSchema),
    monitoring_state: monitoringStateSchema,
    plan_revision_ids: z.array(idSchema("plr")),
    health: sourceHealthSchema.nullable(),
    pause_id: idSchema("pau").optional(),
    complete_through: instantSchema.nullable(),
    complete_back_to: instantSchema.nullable(),
    last_successful_enumeration_at: instantSchema.nullable(),
    ...accessFields,
    revision: z.number().int().positive(),
  })
  .refine((s) => (s.monitoring_state === "monitored") === (s.health !== null), {
    message: "health is computed for monitored sources only",
    path: ["health"],
  })
  .refine((s) => (s.monitoring_state === "paused") === (s.pause_id !== undefined), {
    message: "a paused source names its pause, and only a paused source does",
    path: ["pause_id"],
  });
export type SourceRecord = z.infer<typeof sourceRecordSchema>;

export const SCAN_KINDS = ["scheduled", "scan_now", "catch_up", "backfill"] as const;
export const scanKindSchema = z.enum(SCAN_KINDS);

export const SCAN_STATES = ["queued", "running", "complete", "partial", "failed"] as const;
export const scanStateSchema = z.enum(SCAN_STATES);

export const scanSlotSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}Z$/);

export const scanRecordSchema = z
  .strictObject({
    id: idSchema("scn"),
    source_id: idSchema("src"),
    slot: scanSlotSchema,
    workflow_id: z.string().min(1),
    kind: scanKindSchema,
    requested_by: actorSchema.optional(),
    state: scanStateSchema,
    interval: intervalSchema,
    pages_visited: countSchema,
    complete_boundary_reached: z.boolean(),
    stop_reason: z.string().min(1).optional(),
    items_discovered: z.array(idSchema("itm")),
    gap_id: idSchema("gap").optional(),
    attempt_ids: z.array(idSchema("att")),
    started_at: instantSchema.nullable(),
    finished_at: instantSchema.nullable(),
  })
  .refine((s) => s.workflow_id === `source:${s.source_id}:${s.slot}`, {
    message: "workflow_id is source:<source id>:<slot>",
    path: ["workflow_id"],
  })
  .refine((s) => s.state !== "complete" || s.complete_boundary_reached, {
    message: "a complete scan reached the previous boundary",
    path: ["complete_boundary_reached"],
  });
export type ScanRecord = z.infer<typeof scanRecordSchema>;

export const GAP_HANDLINGS = ["automatic", "needs_person"] as const;
export const gapHandlingSchema = z.enum(GAP_HANDLINGS);

export const GAP_NEXT_STEPS = [
  "automatic_retry",
  "backfill_queued",
  "backfill_running",
  "catch_up_queued",
  "wait_for_source",
  "accept_as_permanent",
  "get_access",
  "not_listed",
] as const;
export const gapNextStepSchema = z.enum(GAP_NEXT_STEPS);

export const GAP_STATES = ["open", "resolved"] as const;
export const gapStateSchema = z.enum(GAP_STATES);

export const GAP_RESOLUTIONS = [
  "closed_by_scan",
  "closed_by_backfill",
  "accepted_permanent",
  "resolved_with_note",
] as const;
export const gapResolutionKindSchema = z.enum(GAP_RESOLUTIONS);

export const gapSubjectSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("source_interval"), source_id: idSchema("src"), interval: intervalSchema }),
  z.strictObject({ kind: z.literal("known_appearance"), item_id: idSchema("itm") }),
]);

export const gapResolutionSchema = z
  .strictObject({
    kind: gapResolutionKindSchema,
    at: instantSchema,
    by: actorSchema,
    note: z.string().min(1).nullable(),
    evidence: z
      .union([
        z.strictObject({ backfill_id: idSchema("bfl") }),
        z.strictObject({ scan_id: idSchema("scn") }),
        z.strictObject({ attempt_id: idSchema("att") }),
      ])
      .nullable(),
  })
  .refine((r) => (r.kind !== "accepted_permanent" && r.kind !== "resolved_with_note") || r.note !== null, {
    message: "accepting or resolving a gap by hand needs a note",
    path: ["note"],
  });

export const gapRecordSchema = z
  .strictObject({
    id: idSchema("gap"),
    subject: gapSubjectSchema,
    reason: z.string().min(1),
    handling: gapHandlingSchema,
    next_step: gapNextStepSchema,
    state: gapStateSchema,
    opened_at: instantSchema,
    recovery_progress: z.strictObject({ intervals_read: countSchema, intervals_total: countSchema }).nullable(),
    task_id: idSchema("tsk").nullable(),
    resolution: gapResolutionSchema.nullable(),
  })
  .refine((g) => (g.state === "resolved") === (g.resolution !== null), {
    message: "a resolved gap, and only a resolved gap, has a resolution",
    path: ["resolution"],
  })
  .refine((g) => g.handling !== "needs_person" || g.task_id !== null, {
    message: "a gap that needs a person has a task",
    path: ["task_id"],
  });
export type GapRecord = z.infer<typeof gapRecordSchema>;

export const ROUTE_STRATEGIES = ["data_api", "feed", "direct", "via_proxy", "browser", "supplier_api"] as const;
export const routeStrategySchema = z.enum(ROUTE_STRATEGIES);

export const ROUTE_EGRESSES = ["direct", "webshare_subuser"] as const;
export const routeEgressSchema = z.enum(ROUTE_EGRESSES);

export const ROUTE_STATES = ["active", "cooling", "quarantined", "releasing"] as const;
export const routeStateSchema = z.enum(ROUTE_STATES);

export const routeQuotaSchema = z.strictObject({
  group: slugSchema,
  daily_units: countSchema,
  used_units: countSchema,
  resets_at: instantSchema,
});

export const routeRecordSchema = z
  .strictObject({
    id: idSchema("rte"),
    platform: platformSchema,
    strategy: routeStrategySchema,
    egress: routeEgressSchema,
    ladder_position: z.number().int().positive(),
    credential_ref: z
      .string()
      .regex(/^env:[A-Z][A-Z0-9_]*$/)
      .nullable(),
    state: routeStateSchema,
    state_since: instantSchema,
    state_reason: z.string().min(1).nullable(),
    quarantined_by: actorSchema.optional(),
    release_requested_by: actorSchema.optional(),
    task_id: idSchema("tsk").optional(),
    cooling_until: instantSchema.nullable(),
    quota: routeQuotaSchema.optional(),
    revision: z.number().int().positive(),
  })
  .refine((r) => (r.state === "cooling") === (r.cooling_until !== null), {
    message: "a cooling route, and only a cooling route, has an end time",
    path: ["cooling_until"],
  })
  .refine((r) => r.state === "active" || r.state_reason !== null, {
    message: "a route that is not active has a reason",
    path: ["state_reason"],
  });
export type RouteRecord = z.infer<typeof routeRecordSchema>;

export const PLATFORM_ISSUE_CAUSES = ["quota", "rate_limit", "anti_bot_wave", "outage", "proxy_configuration"] as const;
export const platformIssueCauseSchema = z.enum(PLATFORM_ISSUE_CAUSES);

export const PLATFORM_ISSUE_STATES = ["open", "resolved"] as const;
export const platformIssueStateSchema = z.enum(PLATFORM_ISSUE_STATES);

export const platformIssueRecordSchema = z
  .strictObject({
    id: idSchema("pwi"),
    platform: platformSchema,
    cause: platformIssueCauseSchema,
    state: platformIssueStateSchema,
    opened_at: instantSchema,
    resolved_at: instantSchema.nullable(),
    affected_source_ids: z.array(idSchema("src")).min(1),
    affected_profile_ids: z.array(idSchema("prf")),
    evidence: z.union([
      z.strictObject({ proxies_cooling: countSchema, proxies_total: z.number().int().positive() }),
      z.strictObject({ quota_group: slugSchema }),
      z.strictObject({ note: z.string().min(1) }),
    ]),
    expected_reset: instantSchema.nullable(),
    next_step: z.string().min(1),
    task_id: idSchema("tsk"),
  })
  .refine((p) => (p.state === "resolved") === (p.resolved_at !== null), {
    message: "a resolved issue, and only a resolved issue, has a resolution time",
    path: ["resolved_at"],
  });
export type PlatformIssueRecord = z.infer<typeof platformIssueRecordSchema>;

export const HISTORY_JOB_STATES = [
  "previewed",
  "queued",
  "running",
  "waiting",
  "paused",
  "completed",
  "cancelled",
] as const;
export const historyJobStateSchema = z.enum(HISTORY_JOB_STATES);

export const HISTORY_SOURCE_STATES = [...HISTORY_JOB_STATES, "unreachable"] as const;
export const historySourceStateSchema = z.enum(HISTORY_SOURCE_STATES);

const historyJobFields = {
  profile_id: idSchema("prf").nullable(),
  sources: z
    .array(
      z.strictObject({
        source_id: idSchema("src"),
        interval: intervalSchema,
        state: historySourceStateSchema,
        complete_back_to: instantSchema.nullable(),
      }),
    )
    .min(1),
  state: historyJobStateSchema,
  preview: z.strictObject({
    expected_items: countSchema,
    cost_estimate: moneySchema,
    storage_growth_bytes: countSchema,
    unreachable_source_ids: z.array(idSchema("src")),
  }),
  budget_category_id: idSchema("bud"),
  confirmed_by: actorSchema.nullable(),
  confirmed_at: instantSchema.nullable(),
  paused: actionNoteSchema.nullable(),
  cancelled: actionNoteSchema.nullable(),
  completed_at: instantSchema.nullable(),
};

export const historyJobRecordSchema = z
  .discriminatedUnion("kind", [
    z.strictObject({
      id: idSchema("bfl"),
      kind: z.literal("backfill"),
      trigger: z.discriminatedUnion("kind", [
        z.strictObject({ kind: z.literal("gap"), gap_id: idSchema("gap") }),
        z.strictObject({ kind: z.literal("catch_up"), gap_id: idSchema("gap") }),
        z.strictObject({ kind: z.literal("plan_revision"), plan_revision_id: idSchema("plr") }),
      ]),
      ...historyJobFields,
    }),
    z.strictObject({
      id: idSchema("ext"),
      kind: z.literal("history_extension"),
      trigger: z.strictObject({ kind: z.literal("editor") }),
      added_interval: intervalSchema,
      ...historyJobFields,
    }),
  ])
  .refine((j) => (j.confirmed_at === null) === (j.confirmed_by === null), {
    message: "confirmation has an actor and a time",
    path: ["confirmed_by"],
  })
  .refine((j) => j.state === "cancelled" || (j.state === "previewed") === (j.confirmed_at === null), {
    message: "a previewed job is unconfirmed; every later state is confirmed",
    path: ["confirmed_at"],
  })
  .refine((j) => (j.state === "paused") === (j.paused !== null), {
    message: "paused state and pause record go together",
    path: ["paused"],
  })
  .refine((j) => (j.state === "cancelled") === (j.cancelled !== null), {
    message: "cancelled state and cancellation go together",
    path: ["cancelled"],
  })
  .refine((j) => (j.state === "completed") === (j.completed_at !== null), {
    message: "completed state and completion time go together",
    path: ["completed_at"],
  });
export type HistoryJobRecord = z.infer<typeof historyJobRecordSchema>;
